import { type MentaErrorCode } from './errors';
import { Emitter, type Unsubscribe } from './emitter';
import { applyClampedHeight, buildIframeSrc, createIframeElement } from './transport/iframe';
import type { FieldStyleByState, IframeTheme } from './transport/iframe';
import { MessageRouter } from './transport/message-router';
import type { CardErrorCode, FieldRoute, ParentInboundMessage } from '../protocol';

export type Phase = 'idle' | 'mounting' | 'ready' | 'destroyed';

export interface SessionMountOptions {
  /** Which single-input entry point this session's iframe loads
   * (design D1: `pan`/`expiry`/`cvv`; `coordinator` is `FieldGroup`'s own
   * concern, not a `CardFieldSession`). */
  route: FieldRoute;
  /** Shared `groupId` nonce (design D2) so this field joins the same
   * `BroadcastChannel` as its sibling fields and the coordinator. */
  group: string;
  token: string;
  theme?: IframeTheme;
  locale?: string;
  placeholder?: string;
  ariaLabel?: string;
  style?: FieldStyleByState;
}

export interface SessionEvents {
  ready: () => void;
  error: (err: { code: MentaErrorCode; reason?: string }) => void;
  valid: (isValid: boolean) => void;
  resize: (height: number) => void;
}

const LOAD_TIMEOUT_MS = 8000;
const CONTENT_SETTLE_MS = 250;

/** Wire `CardErrorCode` -> public `MentaErrorCode`, per spec's resolved
 * taxonomy (revision 3). `unauthorized` is never produced — see errors.ts
 * and docs/protocol.md. */
function mapWireErrorCode(code: CardErrorCode | undefined): MentaErrorCode {
  switch (code) {
    case 'invalid_card_number':
    case 'invalid_expiry':
    case 'card_expired':
    case 'invalid_cvv':
    case 'invalid_card_details':
      return 'invalid_card';
    case 'network_error':
      return 'network_error';
    case 'request_rejected':
    case 'service_error':
    case 'malformed_response':
    case 'unknown_error':
    case 'iframe_load_timeout':
    case undefined:
      return 'unknown';
    default:
      return 'unknown';
  }
}

/** `CardFieldSession` — narrowed (Phase 3) to own exactly ONE single-input
 * iframe's mount lifecycle: load/settle/ready, per-field validity/error
 * (`field-state`), and resize. Tokenization is no longer a per-field
 * concern — it moved to `FieldGroup.createCardToken()`, a GROUP-level operation
 * spanning all 3 fields plus the coordinator (design: "`createCardToken()`
 * moves here from `session.ts`"). `CardFieldHandle` is the thin public
 * facade over this session. */
export class CardFieldSession {
  private currentPhase: Phase = 'idle';
  private iframe: HTMLIFrameElement | undefined;
  private loadTimer: ReturnType<typeof setTimeout> | undefined;
  private settleTimer: ReturnType<typeof setTimeout> | undefined;
  private loaded = false;
  /** True when `theme.height` was declared at mount() — fixes the iframe's
   * height for its whole lifetime and makes `content-resize` a no-op for
   * sizing purposes (Mercado-Pago-like declared-size model). */
  private hasDeclaredHeight = false;
  private readonly emitter = new Emitter<SessionEvents>();

  constructor(
    private readonly origin: string,
    private readonly router: MessageRouter,
    private readonly siteKey: string,
  ) {}

  get phase(): Phase {
    return this.currentPhase;
  }

  on<K extends keyof SessionEvents>(event: K, callback: SessionEvents[K]): Unsubscribe {
    // TypeScript cannot correlate a generic `K` across this wrapper and the
    // underlying `Emitter`'s own generic `on` (the well-known "correlated
    // union" limitation); the public signature above stays fully typed.
    return this.emitter.on(event, callback as never);
  }

  mount(container: HTMLElement, options: SessionMountOptions): void {
    this.currentPhase = 'mounting';
    this.loaded = false;

    const src = buildIframeSrc(this.origin, {
      route: options.route,
      group: options.group,
      token: options.token,
      siteKey: this.siteKey,
      nonce: Date.now(),
      theme: options.theme,
      locale: options.locale,
      placeholder: options.placeholder,
      ariaLabel: options.ariaLabel,
      style: options.style,
    });
    this.hasDeclaredHeight = options.theme?.height !== undefined;
    const iframe = createIframeElement(src, {
      height: options.theme?.height,
      width: options.theme?.width,
    });
    container.replaceChildren(iframe);
    this.iframe = iframe;

    iframe.addEventListener('load', () => this.handleLoad());

    this.loadTimer = setTimeout(() => this.handleLoadTimeout(), LOAD_TIMEOUT_MS);

    if (iframe.contentWindow) {
      this.router.register(this.origin, iframe.contentWindow, (message) => this.handleMessage(message));
    }
  }

  unmount(): void {
    if (this.currentPhase === 'destroyed') {
      return;
    }

    this.clearTimers();
    if (this.iframe?.contentWindow) {
      this.router.unregister(this.iframe.contentWindow);
    }
    this.iframe?.remove();
    this.iframe = undefined;
    this.currentPhase = 'destroyed';
  }

  /** Test-only accessor for the mounted iframe element, so tests can
   * dispatch synthetic `load`/`message` events against it. */
  getIframeElementForTest(): HTMLIFrameElement {
    if (!this.iframe) {
      throw new Error('CardFieldSession: no iframe mounted');
    }
    return this.iframe;
  }

  private handleLoad(): void {
    this.loaded = true;
    if (this.loadTimer) clearTimeout(this.loadTimer);
    if (this.currentPhase !== 'mounting') return;

    this.settleTimer = setTimeout(() => this.transitionToReady(), CONTENT_SETTLE_MS);
  }

  private handleLoadTimeout(): void {
    if (this.loaded || this.currentPhase !== 'mounting') return;
    this.emitter.emit('error', { code: 'timeout' });
    this.transitionToReady();
  }

  private transitionToReady(): void {
    if (this.currentPhase === 'destroyed') return;
    this.currentPhase = 'ready';
    this.emitter.emit('ready');
  }

  private handleMessage(message: ParentInboundMessage): void {
    if (message.type === 'field-state') {
      this.emitter.emit('valid', message.valid);
      if (!message.valid && message.code !== undefined) {
        this.emitter.emit('error', { code: mapWireErrorCode(message.code) });
      }
      return;
    }

    if (message.type === 'content-resize') {
      // Declared height (theme.height) wins outright: ignore the real
      // measurement entirely — no clamp, no style write, no `resize` event —
      // so the iframe stays fixed at the consumer-declared size regardless
      // of what src/inputs actually measures.
      if (this.hasDeclaredHeight) return;

      const clamped = this.iframe ? applyClampedHeight(this.iframe, message.height) : message.height;
      this.emitter.emit('resize', clamped);
    }
  }

  private clearTimers(): void {
    if (this.loadTimer) clearTimeout(this.loadTimer);
    if (this.settleTimer) clearTimeout(this.settleTimer);
  }
}
